import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { GeneralButton } from 'sharedComponents/GeneralButton';
import useNewCapability from 'sharedHooks/useNewCapability';
import { translate } from 'helpers/translateHelper';
import './style.scss';
import { BEARING_TYPES } from 'sharedComponents/Chart/constants';
import IconEchartBearing from 'svgIcons/MotionPortalIcons/IconEchartBearing';
import EchartMoreGraphSetting from './EchartMoreGraphSetting';
import { assetType } from 'helpers/constants';
import { echartViews } from './constants';
import { debounce } from 'lodash';

const EchartRightPanel = ({
    selectedEchartBandSettings,
    setEchartBandSettings,
    sensorType,
    selectedEchartView,
}) => {

    const isMSRasset = sensorType === assetType.MACHSENSOR;
    const newCapabilities = useNewCapability();
    const hasFrequencyViewCapability = newCapabilities.hasFrequencyViewCapability();

    const toggleControl = useCallback(debounce((controlType, value) => {
        if (controlType === 'bearingTypes') {
            if (!isMSRasset) {
                setEchartBandSettings({
                    ...selectedEchartBandSettings,
                    bearingTypes: selectedEchartBandSettings.bearingTypes.includes(value)
                        ? selectedEchartBandSettings.bearingTypes.length > 1
                            ? selectedEchartBandSettings.bearingTypes.filter(type => type !== value)
                            : selectedEchartBandSettings.bearingTypes
                        : selectedEchartBandSettings.bearingTypes.length < 2
                            ? [...selectedEchartBandSettings.bearingTypes, value]
                            : selectedEchartBandSettings.bearingTypes
                });
            } else {
                setEchartBandSettings({
                    ...selectedEchartBandSettings,
                    bearingTypes: [value]
                });
            }
        } else {
            setEchartBandSettings({
                ...selectedEchartBandSettings,
                [controlType]: !selectedEchartBandSettings[controlType]
            });
        }
    }, 300), [isMSRasset, selectedEchartBandSettings, setEchartBandSettings]);

    const handleLegendToggle = useCallback((controlType, value) => {
        toggleControl(controlType, value);
    }, [toggleControl]);

    const isShowBearingControls = selectedEchartView === echartViews.ORDER_OF_SPEED || selectedEchartView === echartViews.FREQUENCY && hasFrequencyViewCapability;

    return (
        <div className='echart-right-panel'>
            <div className='graph-sections'>
                {isShowBearingControls &&
                    <div className='bearing-section'>
                        <div className='section-title'>
                            {translate('ABB.Powertrain.Frontend.bearingLabel')}:
                        </div>

                        <div className={`bearing-types ${isMSRasset ? 'msr-asset' : ''}`}>
                            <GeneralButton
                                className={`${BEARING_TYPES.DE}-type${selectedEchartBandSettings.bearingTypes.includes(BEARING_TYPES.DE) ? ' selected' : ''}`}
                                type='normal'
                                text={translate('ABB.Powertrain.Frontend.bearingTypeDE')}
                                disabled={false}
                                onClick={() => handleLegendToggle('bearingTypes', BEARING_TYPES.DE)}
                            />
                            <GeneralButton
                                className={`${BEARING_TYPES.NDE}-type${selectedEchartBandSettings.bearingTypes.includes(BEARING_TYPES.NDE) ? ' selected' : ''}`}
                                type='normal'
                                text={translate('ABB.Powertrain.Frontend.bearingTypeNDE')}
                                disabled={false}
                                onClick={() => handleLegendToggle('bearingTypes', BEARING_TYPES.NDE)}
                            />
                        </div>
                    </div>
                }
                {selectedEchartView === echartViews.ORDER_OF_SPEED &&
                    <div className='band-options'>
                        <div className='divider-line first-divider' />
                        <div className='bearing-controls'>
                            <GeneralButton
                                className={selectedEchartBandSettings.bsfType ? 'selected' : ''}
                                type='normal'
                                text={translate('ABB.Powertrain.Frontend.bearingFaultBSF')}
                                icon={<IconEchartBearing color={'rgba(78, 121, 167, 1)'} />}
                                onClick={() => handleLegendToggle('bsfType')}
                            />
                            <GeneralButton
                                className={selectedEchartBandSettings.bpfoType ? 'selected' : ''}
                                type='normal'
                                text={translate('ABB.Powertrain.Frontend.bearingFaultBPFO')}
                                icon={<IconEchartBearing color={'rgba(89, 161, 79, 1)'} />}
                                onClick={() => handleLegendToggle('bpfoType')}
                            />
                            <GeneralButton
                                className={selectedEchartBandSettings.bpfiType ? 'selected' : ''}
                                type='normal'
                                text={translate('ABB.Powertrain.Frontend.bearingFaultBPFI')}
                                icon={<IconEchartBearing color={'rgba(242, 142, 44, 1)'} />}
                                onClick={() => handleLegendToggle('bpfiType')}
                            />
                            {/* <GeneralButton
                                className={selectedEchartBandSettings.cage ? 'selected' : ''}
                                type='normal'
                                text={translate('ABB.Powertrain.Frontend.fftEchartCage')}
                                icon={<IconEchartBearing color={'rgba(176, 122, 161, 1)'} />}
                                onClick={() => handleLegendToggle('cage')}
                            /> */}
                            <GeneralButton
                                className={selectedEchartBandSettings.harmonics ? 'selected' : ''}
                                type='normal'
                                text={translate('ABB.Powertrain.Frontend.fftEchartHarmonics')}
                                icon={<IconEchartBearing color={'rgba(0, 0, 0, 1)'} />}
                                onClick={() => handleLegendToggle('harmonics')}
                            />
                        </div>
                        <div className='divider-line' />
                        <div className='bands-only'>
                            <GeneralButton
                                className={selectedEchartBandSettings.bandsOnly ? 'selected' : ''}
                                type='normal'
                                text={translate('ABB.Powertrain.Frontend.fftEchartBandsOnly')}
                                onClick={() => handleLegendToggle('bandsOnly')}
                            />
                        </div>
                    </div>
                }
            </div>
            <EchartMoreGraphSetting
                selectedEchartBandSettings={selectedEchartBandSettings}
                setEchartBandSettings={setEchartBandSettings}
                selectedEchartView={selectedEchartView}
            />
        </div >
    );
};

EchartRightPanel.propTypes = {
    setEchartBandSettings: PropTypes.func.isRequired,
    selectedEchartBandSettings: PropTypes.shape({
        bandsOnly: PropTypes.bool,
        harmonics: PropTypes.bool,
        bsfType: PropTypes.bool,
        bpfiType: PropTypes.bool,
        bpfoType: PropTypes.bool,
        bearingTypes: PropTypes.arrayOf(PropTypes.string),
        moreGraph: PropTypes.object
    }),
    sensorType: PropTypes.string,
    selectedEchartView: PropTypes.string,
    containerRef: PropTypes.object
};

EchartRightPanel.defaultProps = {
    selectedEchartBandSettings: {
        bandsOnly: false,
        harmonics: false,
        bsfType: false,
        bpfiType: false,
        bpfoType: false,
        bearingTypes: [],
        moreGraph: {}
    },
    sensorType: null,
    selectedEchartView: null,
    containerRef: null
};

export default EchartRightPanel;
